// Document dates default to the SHOP's today (utils/date), never the browser's.
import { RandomCode, storeDayStartUtc, storeNow, storeTodayYmd, toRangeStartUtc } from "./Method";
import { uuid } from "../utils/misc";
import {
  ITEM_IMG_WIDTH,
  LETTERHEAD_MM,
  LOGO_WIDTH,
  RECEIPT_WIDTH,
} from "../constants/receiptSizing";
import {
  ADJUSTMENT_TYPE,
  COUNTER_DEFAULT_REASON_ID,
} from "../constants/adjustmentReasons";

import { useAppContext } from "context";

export default function Model() {
  const { selCompany, bizContext } = useAppContext();
  const ctx = { outletId: bizContext?.outletId || "", companyId: selCompany };

  const JVMdl = () => ({
    id: "",
    isPosted: false,
    title: "",
    type: 1,
    status: "",
    ledgerId: "",
    ...ctx,

    // The shop's midnight today: a voucher is dated on a day of the shop's calendar, and the
    // browser's "now" fell on the next day for anyone working from a zone ahead of the shop.
    voucherDate: storeDayStartUtc(),
    voucherNumber: "",
    reference: "",
    remarks: "",
    docs: [],
    docsFiles: [],
    lines: [
      {
        id: "",
        key: Date.now(),
        chartOfAccountId: "",
        debit: "",
        credit: "",
        particulars: "",
        balanceType: "",
        // Set only on the receivable / payable account (see SelectParty).
        customerId: "",
        supplierId: "",
      },
      {
        id: "",
        key: Date.now() + 1,
        chartOfAccountId: "",
        debit: "",
        credit: "",
        particulars: "",
        balanceType: "",
        customerId: "",
        supplierId: "",
      },
    ],
    // Not Mapped
    isBalanced: true,
    difference: 0,
    totalDebit: 0,
    totalCredit: 0,
    jvAuditTrail: {
      action: "",
      comment: "",

      // Default set
      toStatus: 1,
      fromStatus: 1,
    },
  });

  const BizContextMdl = () => ({
    orgId: "",
    companyId: "",
    outletId: "",
  });

  const LoginMdl = () => ({
    username: "",
    password: "",
  });

  const InvItemCountedMdl = () => {
    return {
      id: "",
      invCountId: "",
      invCountItemId: "",
      expected: "",
      counted: "",
      ...ctx,
    };
  };

  const InvCountMdl = () => {
    return {
      id: "",
      startDate: "",
      startTime: "",
      countName: "",
      countType: 1,
      includeInactiveProds: false,
      items: [],
      ...ctx,
    };
  };

  const FiscalYearMdl = () => ({
    id: "",
    title: "",
    notes: "",
    startDate: "",
    endDate: "",
    fiscalYearDocs: [],
    ...ctx,
  });

  const SnapshotMdl = () => ({
    id: "",
    type: 5,
    fiscalYearId: "",

    fromDate: "",
    toDate: "",

    notes: "",

    totalSales: "",
    totalReturns: "",
    netRevenue: "",
    cogs: "",
    grossProfit: "",
    totalExpenses: "",
    netProfit: "",

    closingInventoryValue: "",
    closingInventoryCount: "",

    TotalReceivables: "",
    TotalPayables: "",

    TotalTaxCollected: "",
    TotalTaxPaid: "",
    TotalDiscount: "",
    ...ctx,
  });

  const PassResetMdl = () => ({
    userId: "",
    newPassword: "",
    confirmPassword: "",
    ...ctx,
  });

  const PinSetMdl = () => ({
    userId: "",
    pin: "",
    confirmPin: "",
    ...ctx,
  });

  const FollowUpMdl = () => ({
    id: 0,
    followUpDate: "",
    followUpTime: "",
    followUpTypeId: "",
    note: "",
    ...ctx,
  });

  const LedgerMdl = () => ({
    id: 0,
    name: "",
    ledgerType: "",
    accountingStandard: "",
    currencyId: "",
    companyId: "",

    isActive: true,
    isDefault: false,

    startDate: new Date().toISOString(),
    endDate: "",

    orgId: "",
    createdAt: new Date().toDateString(),
    createUserId: "",

    updatedAt: "",
    lastUpdatedUser: "",
  });

  const AddressBookMdl = () => ({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });

  const CompanyMdl = () => ({
    id: 0,
    businessType: 0,
    name: "",
    legalName: "",
    regNumber: "",
    tinOrVatRegNo: "",
    timeZoneId: "",

    defaultLedgerId: "",
    defaultCurrencyId: "",

    writeOffLimit: "",
    enableAuthCode: false,
    enablePurchaseInvoice: false,
    journalVoucherAutoPost: false,
    // Desktop app: days a saved PIN / password works offline. Server range 1-90.
    tillOfflineLoginDays: 14,
    // Installment (layaway) terms - the server's defaults, so a new company form
    // shows what an unsaved company would be given.
    installmentEnabled: true,
    installmentMinDepositPercent: 10,
    installmentDays: 60,
    installmentCancellationFeeType: 0,
    installmentCancellationFee: 0,

    useSimpleStockDocNumbering: false,
    stockOrderNoStart: 1000,
    stockReturnNoStart: 1000,
    inventoryAdjustmentNoStart: 1000,
    stockTransferNoStart: 1000,

    isActive: true,
    email: "",
    telephone: "",
    mobile: "",
    website: "",
    socialMedia: "",
    registeredAddress: AddressBookMdl(),
    sameAsRegAddress: false,
    mailingAddress: AddressBookMdl(),

    initialLedger: LedgerMdl(),

    logo: "",
    logoFile: "",

    orgId: "",
    createAt: new Date().toISOString(),
    createUserId: "",

    updatedAt: "",
    lastUpdateUserId: "",
  });

  const PrintLabelMdl = () => ({
    id: "",
    printType: 1,
    items: [],

    showOutlet: true,
    showSku: true,
    showProdTitle: true,
    showRtlPrice: true,
    showMadeIn: false,

    outletName: "",

    createDate: new Date().toISOString(),
    ...ctx,
  });

  // The ASK. Note what is absent: no number, no status, no date — the server owns
  // all three. fromOutletId is the branch being asked; toOutletId is us.
  const StockRequestMdl = () => ({
    id: "",
    // NOTE the inversion: the form labels toOutletId "From" (the branch asking) and
    // fromOutletId "To" (the branch being asked). The columns are named for the way
    // the STOCK moves, the form for the way the REQUEST moves. See the header block
    // in Request/Form/GeneralFields.jsx.
    fromOutletId: "",
    toOutletId: bizContext?.outletId || "",
    // Display only — name the two branches in the banner. Stripped from the payload
    // in the form header; neither is part of the document.
    fromOutletLabel: "",
    toOutletLabel: bizContext?.outletLabel || "",
    requiredByDate: null,
    note: "",
    items: [],
    // One GUID per form, reused across every retry, so a double-tap on a slow
    // connection cannot raise the same request twice.
    clientRequestId: crypto.randomUUID(),
    companyId: selCompany,
  });

  // The SEND. Mirrors StockRequestMdl, and its fields are named for the way the STOCK
  // moves — there is no inversion to unpick here the way there is on the request.
  //
  // Against a request the server overwrites BOTH branches from the request itself
  // (StockTransferService.Dispatch), so what is held here is only what the direct-push
  // door needs. fromLocationId defaults to the branch the user is working in; it is a
  // hint, not authority — PermittedOutletIdsAsync is what actually decides.
  const StockDispatchMdl = () => ({
    transferRequestId: "",
    fromLocationId: bizContext?.outletId || "",
    toLocationId: "",
    remarks: "",
    items: [],
    // One GUID per form, reused across every retry: a double-tap on a slow connection
    // must not ship the same consignment twice.
    clientRequestId: crypto.randomUUID(),
    companyId: selCompany,
  });

  const InvAdjustmentMdl = () => ({
    id: "",
    adjustmentNo: "",
    adjustmentDate: storeDayStartUtc(),
    adjustmentType: 1,
    reason: 2,
    note: "",
    adjustmentItems: [],
    totalQty: 0,
    totalAmount: 0,
    // One GUID per form, reused across every retry: a double-click on Confirm
    // must not post the stock and the journal twice.
    clientRequestId: crypto.randomUUID(),
    ...ctx,
  });

  // Counter stock wastage. Mirrors WastageInputDto: no adjustmentType, no
  // adjustmentDate and no unit costs, because the server derives all three.
  // outletId is carried only so the product search and stock lookup scope to the
  // right till — the post does not send it, and the server would ignore it if it
  // did. sellRegisterId is likewise not held here: StockWastageHeader reads it
  // straight off the active register at submit time.
  const StockWastageMdl = () => ({
    id: "",
    // Pre-selected to Wastage — the overwhelmingly common counter case. Still a
    // whitelisted value, so the server accepts it as-is if left untouched.
    reason: COUNTER_DEFAULT_REASON_ID,
    note: "",
    adjustmentItems: [],
    adjustmentType: ADJUSTMENT_TYPE.decrease,
    clientRequestId: crypto.randomUUID(),
    ...ctx,
  });

  const SellReturnMdl = () => ({
    id: "",
    sellId: "",
    returnItems: [],
    refundAmt: 0,
    refundTaxAmt: 0,
    discAdj: 0,
    payableRefund: 0,
    storeCredit: "",
    refundMethods: [],
    // No returnDate — the server stamps it (SellReturn.ReturnDate is [BindNever]).
    ...ctx,
  });

  const DirectReturnMdl = () => ({
    id: "",
    customerId: "",
    returnItems: [],
    refundAmt: 0,
    refundTaxAmt: 0,
    discAdj: 0,
    storeCredit: "",
    refundMethods: [],
    isDirectReturn: true,
    // No returnDate — server-stamped, same as SellReturnMdl above.
    ...ctx,
  });

  const CustomerTxnMdl = () => ({
    id: "",
    isPaid: "",
    sellType: "",
    txnType: "",
    sellId: "",
    paySellIds: "",
    customerId: "",
    cashierId: "",
    registerId: "",
    orgId: "",
    amount: "",
    payTypeId: "",
    userNote: "",
    referenceNo: "",
    createdAt: new Date().toISOString(),
    ...ctx,
  });

  const SupplierTxnMdl = () => ({
    id: "",
    isPaid: "",
    txnType: "",
    stockId: "",
    supplierId: "",
    txnUserId: "",
    orgId: "",
    amount: "",
    payTypeId: "",
    // Which physical cash safe funded the payment. "" = not from a safe, and the
    // GL account is derived from payTypeId as it always was.
    cashSafeId: "",
    userNote: "",
    referenceNo: "",
    writeOffRemaining: false,
    writeOffReason: "",
    payStockIds: "",
    payInvoiceIds: "",
    createDate: new Date().toISOString(),
    ...ctx,
  });

  // Opening a till. Its own model, NOT SellRegisterMdl: the open endpoint takes
  // OpenRegDto and every closing-side field on the shared model would either be ignored
  // or — as it was until this shipped — bound straight onto the new session row.
  //
  // The two amounts are separate because the money is: countedCash is what is already in
  // the drawer and moves nowhere, topUpAmount is what is being brought in and is the
  // only figure with a source. The opening float is their sum, derived at the edge.
  const OpenRegMdl = () => ({
    registerId: "",
    outletId: "",

    countedCash: "",
    // The echo the server checks the count against. Written from GetOpenRegContext, never
    // typed — a stale value here is a 409, not a silently wrong variance.
    expectedCarriedOver: 0,
    varianceReason: "",

    addingCash: false,
    topUpAmount: "",
    topUpSource: "",
    cashSafeId: "",
    handedByUserId: "",

    openingNotes: "",
  });

  const SellRegisterMdl = () => ({
    id: 0,

    registerId: "",
    userId: "",

    cashInDrawer: "",
    openingNotes: "",
    openingTime: "",

    // Where the float came from. Left blank on purpose: an unanswered source keeps the
    // pre-existing server behaviour, so a screen that never asks cannot accidentally
    // assert "left in the drawer" and skip the journal entry.
    openingSourceKind: "",
    openingCashSafeId: "",
    openingHandedByUserId: "",

    cashMovement: 0,
    closingTime: "",
    closingNotes: "",
    closingFloat: 0,

    // Where the handed-over cash went. Same blank-means-unanswered rule.
    closingDestKind: "",
    closingCashSafeId: "",
    closingHandedToUserId: "",
    cashReceived: "",
    sellRegPayments: [],
    ...ctx,
  });

  const CoaMdl = () => ({
    id: 0,
    code: "",
    name: "",
    localName: "",

    accountType: "",
    accountCategory: "",
    normalBalance: "",

    isActive: true,
    isPostable: true,
    isFrozen: false,

    parentAccountId: "",
    level: 0,

    ledgerId: "",
    companyId: "",
    currencyId: "",

    effectiveFrom: new Date().toUTCString(),
    effectiveTo: "",

    description: "",
    openingBalance: "",
    openingBalanceType: "",
    rowVersion: "",
  });

  const AccountMappingMdl = () => ({
    id: 0,
    isActive: true,
    mappingType: "",
    accountId: "",
    ledgerId: "",
    companyId: "",
    orgId: "",
    effectiveFrom: new Date().toUTCString(),
    effectiveTo: "",
    rowVersion: "",
  });

  const ChgPassMdl = () => ({
    oldPass: "",
    password: "",
    confirmPass: "",
  });

  const RegisterMdl = () => ({
    id: 0,
    orgId: "",
    userId: "",
    name: "",
    emailReceipt: false,
    printReceipt: true,
    enableSound: true,
    accountId: "",
    // A till's drawer account is the debit leg of every open-float entry and the
    // credit leg of every close-handover, so a register must own one. Defaulting to
    // "make it for me" is what stops people skipping it and silently collapsing the
    // drawer onto the shared 1111 Cash on Hand.
    autoCreateAccount: true,
    // Pre-selects the safe on the open/close cash question for this till. Blank = fall
    // back to the outlet-then-shared resolution, which is right for the many tenants
    // running one safe per branch.
    defaultCashSafeId: "",
    // How far the drawer count at open may differ from what the last shift left before
    // a supervisor has to approve it. Blank = no ceiling, which is NOT the same answer
    // as 0 — RmvEmptyProp drops "" so the server reads it as null and leaves the reason
    // note as the only control. Typing 0 means "every fils needs a signature".
    openingVarianceTolerance: "",
    ...ctx,
  });

  const SellMdl = () => ({
    id: 0,
    status: 2,
    type: "",

    sellItems: [],
    taxAmt: 0,
    taxRate: 0,
    taxId: "",
    disAmt: "",
    discount: "",
    disType: 1,
    subtotal: 0,
    totalItems: 0,
    netPayable: 0,
    received: "",
    change: "",
    note: "",
    customerId: "",
    customerVehicleId: "",
    customerVehicle: null,
    employeeId: "",
    employee: null,
    payments: [],
    // Blank on purpose. This used to hold new Date() from the moment the CART WAS
    // OPENED, and Checkout posted that as the invoice time — so a receipt showed
    // when the first item was scanned, not when the customer paid. The invoice
    // instant is now stamped server-side at payment (SellRepo.ProcessSale); an
    // empty string is dropped from the payload by RmvArryEmptyProp, so nothing is
    // sent. Populated only in edit mode, where it carries the sale's existing date.
    sellDate: "",
    quoteSaleId: "",
    // Set only when this invoice is billing a Sales Order. Validated server-side
    // in SalesInvoiceRepo.Post against the order's tenant, status and customer.
    salesOrderId: "",

    // Client idempotency key — one GUID per cart, reused across every retry of
    // this sale so the server collapses a double-submit into a single invoice.
    // A fresh cart (new SellMdl) gets a new key; on reset after a completed sale
    // the next cart gets its own. See SellRepo.Post idempotency guard.
    clientRequestId: uuid(),

    // Promotion engine denormalised fields (populated by POS coupon input
    // + server-side FinalizeRedemptionsAsync)
    couponCode: "",
    couponDiscountAmt: 0,
    autoDiscountAmt: 0,
    promotionTotalAmt: 0,

    // Optional auth code (e.g. card terminal authorization). Captured only
    // when Company.EnableAuthCode is ON; printed on the receipt when set.
    authCode: "",

    // Sales Invoice — walk-in capture + header extras. Null on POS sales.
    walkInMobile: "",
    walkInCivilId: "",
    walkInName: "",
    walkInAddress: "",
    walkInArea: "",
    refNo: "", // Our Reference — the shop's own job / contract number
    customerRefNo: "", // Customer Reference — their PO number
    priceTier: 1, // Retail by default; matches EnumLi.PriceTier
    invoiceNo: "",

    ...ctx,
  });

  const OrgMdl = () => ({
    // Org Info.
    id: 0,
    name: "",
    defaultTaxId: "",
    currencyId: "",
    logo: "",
    logoFile: "",
    description: "",
    uiTemplate: "modern",
    // Empty means "never chosen" - the server then serves the product default and the
    // desktop till leaves its built-in palette alone. Clearing the picker sends "" and
    // restores that, which is why the field is a string rather than a nullable colour.
    primaryColor: "",
    // Wrong sign-ins in a row before an account is blocked (web + desktop). 0 = off; 3-20.
    maxFailedSignIns: 5,
    // Check credit sales against the customer's credit terms (enabled flag + limit). ON is
    // today's behaviour; OFF never widens who may sell on credit (server rule).
    enableCreditControl: true,

    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
  });

  const CashMgmtMdl = () => ({
    id: "",
    txnType: 1,
    amount: "",
    note: "",
    txnDate: "",
    expenseTypeId: "",
    ...ctx,
  });

  const RefundMdl = () => ({
    id: "",
    prodCond: 1,
    invoiceId: "",
    note: "",
    ...ctx,
  });

  const CartProdMdl = () => ({
    qty: "",
    price: "",
    discount: "",
    note: "",
  });

  const ServiceMdl = () => ({
    id: 0,
    cstmrId: "",
    phone: "",
    email: "",
    date: "",
    hours: "",
    minutes: "",
    note: "",
    ...ctx,
  });

  const ServiceItemMdl = () => ({
    id: 0,
    name: "",
    localName: "",
    skuCode: "",
    desc: "",
    serviceCategoryId: "",
    price: "",
    cost: "",
    isOpenPrice: false,
    minPrice: "",
    taxId: "",
    durationMinutes: "",
    img: "",
    imgFile: "",
    isActive: true,
    ...ctx,
  });

  // Recurring service agreement. billingIntervalCount + billingIntervalUnit encode
  // the frequency (quarterly = 3 Month, yearly = 12 Month) — the UI picks a friendly
  // label and maps to this pair, so a new cadence never needs a migration.
  const ServiceContractMdl = () => ({
    id: 0,
    customerId: "",
    startDate: null,
    endDate: null,
    billingIntervalCount: 1,
    billingIntervalUnit: 2, // Month
    billingAnchorDay: 1,
    dueDayCount: 0,
    dueDateType: 1, // DaysAfterInvoiceDate
    autoRenew: false,
    renewTermMonths: "",
    // Contract-level discount, off the line subtotal and before tax.
    globalDisType: 1, // Percentage
    globalDiscount: 0,
    taxId: "",
    taxRate: "",
    note: "",
    lines: [],
    ...ctx,
  });

  // `key` is a client-side row identity, not a column. New rows are prepended, so every
  // handler addresses a line by key — an index would point at the wrong row the moment
  // the next service is picked. Existing lines get one too when they are loaded.
  const ServiceContractLineMdl = () => ({
    id: 0,
    key: RandomCode(15),
    serviceId: "",
    serviceName: "",
    sku: "",
    description: "",
    qty: 1,
    unitPrice: "",
    disType: 1, // Percentage
    discount: 0,
    taxId: "",
  });

  const ServiceCategoryMdl = () => ({
    id: 0,
    name: "",
    desc: "",
    img: "",
    imgFile: "",
    isActive: true,
    ...ctx,
  });

  const EmployeeMdl = () => ({
    id: 0,
    fullName: "",
    phone: "",
    email: "",
    photo: "",
    photoFile: "",
    jobTitle: "",
    department: "",
    hireDate: "",
    salary: "",
    nationalId: "",
    emergencyContact: "",
    userId: "",
    outletId: "",
    isActive: true,
    ...ctx,
  });

  const ServiceBayMdl = () => ({
    id: 0,
    name: "",
    desc: "",
    isActive: true,
    ...ctx,
  });

  const ServiceDiscountTypeMdl = () => ({
    id: 0,
    name: "",
    discountPercent: "",
    isActive: true,
    ...ctx,
  });

  // Thirty days is the default a quotation is offered for; the cashier can move
  // it. Built from LOCAL date parts, not toISOString(): that is UTC, and for a
  // shop ahead of UTC it hands back yesterday's date for part of every evening.
  const defaultQuoteValidity = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const QuoteMdl = () => ({
    id: 0,
    customerId: "",
    phone: "",
    email: "",
    note: "",
    // "YYYY-MM-DD". A raw dayjs object here serialises to UTC and saves a day
    // early for any zone ahead of UTC.
    validUntil: defaultQuoteValidity(),
    ...ctx,
  });

  const UserMdl = () => ({
    id: 0,
    fullName: "",
    email: "",
    phone: "",

    username: "",
    outletPermissions: [],
    companyPermissions: [],
    roleId: "",
    // Account tier: 0 = Staff, 1 = Client Admin, 2 = System Admin,
    // 3 = System Engineer. Defaults to Staff.
    userType: 0,
    crudProfileId: "",
    reportProfileId: "",
    privilegeProfileId: "",
    restrictToOwnRecords: false,
    recordVisibleFrom: "",
    password: "",
    confirmPass: "",

    dailyTarget: "",
    weeklyTarget: "",
    monthlyTarget: "",

    gender: "",
    dob: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",

    photo: "",
    photoFile: "",
    defaultLang: 1,

    createUser: new Date().toISOString(),
  });

  const BankAccountMdl = () => ({
    id: 0,
    accountName: "",
    bankName: "",
    accountNumber: "",
    iban: "",
    branchCode: "",
    isDefault: false,
    isActive: true,
    chartOfAccountId: "",
    currencyId: "",
    ...ctx,
  });

  const BankReconciliationMdl = () => ({
    id: 0,
    bankAccountId: "",
    statementEndDate: storeTodayYmd(),
    statementEndingBalance: 0,
    beginningBalance: 0,
    serviceCharge: 0,
    serviceChargeAccountId: "",
    interestEarned: 0,
    interestEarnedAccountId: "",
    status: "Draft",
    notes: "",
    ...ctx,
  });

  const PayTypeMdl = () => ({
    id: 0,
    name: "",
    getway: "",
    askForReference: false,
    bankAccountId: "",
    accountId: "",
    ...ctx,
  });

  const TaxMdl = () => ({
    id: 0,
    name: "",
    rate: "",
    createDate: new Date().toISOString(),
    ...ctx,
  });

  const CityMdl = () => ({
    id: 0,
    name: "",
  });

  const OutletMdl = () => ({
    id: 0,
    outletName: "",
    localName: "",
    timeZone: "",
    taxId: "",
    phone: "",
    email: "",
    regName: "",

    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",

    // `posTemplate` is deliberately absent. It chose between two POS shells; the second
    // was never finished and is gone, so the form no longer owns the value. The column
    // stays for the desktop till's sync, and `Outlet.PosTemplate` initialises to 1 in the
    // entity, so an outlet saved without this field keeps 1 rather than dropping to 0.

    // Receipt Settings
    receiptType: 1,
    paymentReceiptType: 1,
    registerReceiptType: 2,
    expenseReceiptType: 1,
    receiptWidth: RECEIPT_WIDTH.def,
    showLogoOnReceipt: false,
    logoWidth: LOGO_WIDTH.thermal.def,
    logoWidthA4: LOGO_WIDTH.a4.def,
    showOutletNameOnReceipt: true,
    showAddressOnReceipt: true,
    // 1 = Top (as it prints today), 2 = Bottom. Mirrors Outlet.AddressPosition.
    addressPosition: 1,
    showPhoneOnReceipt: true,
    showEmailOnReceipt: false,
    showTaxOnReceipt: true,
    showBarcodeOnReceipt: true,
    showThankYouOnReceipt: true,
    // ON by default: these two describe what every receipt already prints, so the
    // default has to be a no-op. Only the shops that want the names off untick them.
    showCashierOnReceipt: true,
    showSalesmanOnReceipt: true,
    // ON for the same reason: the outstanding figure prints on every credit sale today.
    // Mandatory here, not cosmetic — a field missing from this model is `undefined` in
    // the form, RmvEmptyProp strips it on submit, and the server binds `false`.
    showBalanceDueOnReceipt: true,
    // Off by default: this prints something no document showed before, and it depends
    // on product images almost no tenant has uploaded yet.
    showItemImageOnReceipt: false,
    // ON: every thermal receipt prints the SKU line today (same RmvEmptyProp trap).
    showSkuOnThermalReceipt: true,
    // ON: every thermal receipt prints the per-item discount line today (same trap).
    showItemDiscountOnThermalReceipt: true,
    itemImageWidth: ITEM_IMG_WIDTH.def,
    bilingualReceipt: false,
    // Ask the cashier which format to print, per sale, instead of printing
    // receiptType silently. Off by default — nothing changes until an outlet
    // opts in. The Offer* flags say which printers the shop actually has.
    askReceiptFormatOnSale: false,
    offerThermalReceipt: true,
    offerA4Receipt: true,
    offerA5Receipt: false,
    preprintedLetterhead: false,
    letterheadTopMm: LETTERHEAD_MM.top.def,
    letterheadBottomMm: LETTERHEAD_MM.bottom.def,
    allowPosPriceEdit: false,
    enforceMinPriceOnOpenPrice: false,
    enableEmployeeOnSale: false,
    // OFF = a repeat scan adds to the line's quantity, as every branch did before.
    separateCartLines: false,
    // ON: matches the column default — every branch shows the scan box's ALL | BARCODE
    // switch today. OFF hides it and the box always works in All.
    showScanModeSwitch: true,
    // ON: matches the column default — every existing branch sells into negative stock
    // today, and a field missing here posts `false` (RmvEmptyProp trap above).
    allowNegativeStock: true,
    salesTerms: "",
    ...ctx,
  });

  const CstmrGroupMdl = () => ({
    id: 0,
    name: "",
    desc: "",
    createDate: new Date().toISOString(),
    ...ctx,
  });

  const CstmrMdl = () => ({
    id: 0,
    firstName: "",
    lastName: "",
    localName: "",
    email: "",
    phone: "",
    gender: "",
    cstmrGroupId: "",
    dob: "",
    openingBalance: "",

    cityId: "",
    state: "",
    zip: "",
    address1: "",
    address2: "",

    cstmrCode: "",
    company: "",

    website: "",
    twitter: "",
    note: "",
    photo: "",
    photoFile: "",
    accBalType: "",
    balLimit: "",
    taxId: "",

    // Credit Terms
    isCreditEnabled: false,
    creditDays: "",
    creditLimit: "",

    // card details
    nameOnCard: "",
    cardNumber: "",
    expiryDate: "",
    securityCode: "",

    createDate: new Date().toISOString(),
    ...ctx,
  });

  const StockMdl = () => ({
    id: 0,
    supplierId: "",
    orderingFor: [],
    recipientOutletId: "",
    supplierInvoice: "",
    deliveryDate: toRangeStartUtc(storeNow().add(3, "day").format("YYYY-MM-DD")),
    orderNum: RandomCode(12),
    stockProducts: [],
    isReceived: false,
    note: "",
    type: "",

    // Accounts
    subtotal: "",
    discountType: 2,
    discount: "0.00",
    // Not mapped
    discountAmount: "0.00",
    totalItemDisAmt: 0,
    shippingFee: "",
    importDuty: "",
    totalOrderAmount: "",

    createdAt: new Date().toISOString(),
    ...ctx,
  });

  const ReceivedDeliveryMdl = () => ({
    id: 0,
    supplierId: "",
    orderingFor: [],
    recipientOutletId: "",
    supplierInvoice: "",
    deliveryDate: storeDayStartUtc(),
    orderNum: RandomCode(12),
    stockProducts: [],
    note: "",

    // Accounts
    subtotal: "",
    discountType: 2,
    discount: "0.00",
    // Not mapped
    discountAmount: "0.00",
    totalItemDisAmt: 0,
    shippingFee: "",
    importDuty: "",
    totalOrderAmount: "",
    ...ctx,
  });

  // Purchase Invoice / Supplier Bill (3-way match). Header + matched GRN lines.
  const PurchaseInvoiceMdl = () => ({
    id: 0,
    supplierId: "",
    supplierInvoiceNo: "",
    invoiceDate: storeDayStartUtc(),
    dueDate: null,
    currencyId: "",
    note: "",
    postImmediately: true,

    // Matched lines (one per matched GRN line): { goodsReceiptId, goodsReceiptLineId,
    // isVariant, productId, productVariantId, outletId, matchedQty, unitPrice,
    // itemDisAmt, unitId, conversionFactor, ...display }
    lines: [],

    // Accounts (header)
    subtotal: "",
    discountType: 2,
    discount: "0.00",
    discountAmount: "0.00",
    totalItemDisAmt: 0,
    shippingFee: "",
    importDuty: "",
    taxAmount: "0.00",
    totalAmount: "",
    ...ctx,
  });

  const SpecialOrderMdl = () => ({
    id: 0,
    cstmrId: "",
    phone: "",
    email: "",
    deliveryDate: "",
    note: "",
    ...ctx,
  });

  const StockReturnMdl = () => ({
    id: 0,
    orderNo: "",
    stockId: "",
    userId: "",
    stockReturnItems: [],
    keepRefundAsCredit: false,
    totalQty: "",
    totalAmount: "",
    returnDate: storeDayStartUtc(),
    createdAt: new Date().toISOString(),
    note: "",
    ...ctx,
  });

  const ReturnStockMdl = () => ({
    id: 0,
    returnNo: "",
    deliveryDue: new Date().toISOString(),
    deliveryTo: "",
    returnForm: "",
    nameOrRefe: `Return - ${new Date().toDateString()}`,
    supplierInvoice: "",
    files: [],
    returnFiles: [],
    ...ctx,
  });

  const StockTransMdl = () => ({
    id: 0,
    fromOutlet: "",
    toOutlet: "",
    refe: `Stock Transfer 1225 - ${new Date().toDateString()}`,
    date: "",
    ...ctx,
  });

  const SupplierMdl = () => ({
    id: 0,
    splrName: "",
    localName: "",
    defMarkup: "",
    openingBalance: "",
    desc: "",
    supplierCompany: "",
    companyLocalName: "",

    // Credit Terms
    isCreditEnabled: false,
    creditDays: "",
    creditLimit: "",

    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    fax: "",
    gender: "",
    website: "",

    address1: "",
    address2: "",
    country: "",
    city: "",
    state: "",
    zip: "",

    photo: "",
    photoFile: "",
    createDate: new Date().toISOString(),
    ...ctx,
  });

  const CategoryMdl = () => ({
    id: 0,
    name: "",
    desc: "",
    parentCatId: "",
    isMainCat: true,
    img: "",
    imgFile: "",
    createDate: new Date().toISOString(),
    ...ctx,
  });

  const ExpenseTypeMdl = () => ({
    id: 0,
    title: "",
    subtitle: "",
    accountId: "",
    // A type with no account does not fail at posting time — it falls back to the
    // OperatingExpense mapping, so every unlinked type quietly lands on one 5200 line
    // and the P&L loses the breakdown the type was created to give. Defaulting to
    // "make it for me" is what stops people skipping the link.
    autoCreateAccount: true,
    // Which expense group the new account hangs under. Blank = the server's default,
    // the account mapped as Operating Expenses (5200 on a seeded chart).
    parentAccountId: "",
    createDate: new Date().toISOString(),
    ...ctx,
  });

  const ExpenseMdl = () => ({
    id: 0,
    txnDate: storeDayStartUtc(),
    expenseTypeId: "",
    payTypeId: "",
    remarks: "",
    amount: "",
    createDate: new Date().toISOString(),
    ...ctx,
  });

  const BrandMdl = () => ({
    id: 0,
    name: "",
    desc: "",
    createDate: new Date().toISOString(),
    ...ctx,
  });

  // ---------- Promotion engine ----------
  const PromotionMdl = (kind = 1) => ({
    id: 0,
    code: "",
    name: "",
    description: "",
    promoKind: kind, // 1 = Coupon, 2 = AutoDiscount
    rewardType: 1, // 1=Percentage, 2=FixedAmount, 3=Bogo, 4=TieredSpend
    rewardValue: 0,
    maxDiscountCap: "",
    appliesTo: 1, // 1=CartTotal, 2=LineItem, 3=CheapestMatchingItem, 4=SpecificProducts
    buyQty: "",
    getQty: "",
    getRewardType: "",
    getRewardValue: "",
    startAt: new Date().toISOString().slice(0, 16),
    endAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    priority: 100,
    isExclusive: false,
    canStackWithManualDiscount: true,
    canStackWithOtherPromotions: false,
    usageLimitTotal: "",
    usageLimitPerCustomer: "",
    usageLimitPerDay: "",
    requiresApproval: false,
    approvalThresholdPercent: "",
    isActive: true,
    conditions: [],
    tiers: [],
    bogoRules: [],
    companyId: ctx.companyId || 0,
    orgId: ctx.orgId || 0,
  });

  const PromotionConditionMdl = () => ({
    id: 0,
    conditionType: 1,
    operator: 1,
    refEntityId: "",
    valueDecimal: "",
    valueInt: "",
    valueString: "",
    valueJson: "",
  });

  const PromotionTierMdl = () => ({
    id: 0,
    minAmount: 0,
    rewardType: 1,
    rewardValue: 0,
    sortOrder: 0,
  });

  const PriceCategoryMdl = () => ({
    id: 0,
    name: "",
    ...ctx,
  });

  const TagMdl = () => ({
    id: 0,
    name: "",
    desc: "",
    createDate: new Date().toISOString(),
    ...ctx,
  });

  const ForgotMdl = () => ({
    email: "",
    forgotStep: "",
    verCode: "",
    password: "",
    confirmPass: "",
  });

  const ProdMdl = () => ({
    id: 0,
    name: "",
    localName: "",
    brandId: "",
    prodTags: [],
    catId: "",
    subCatId: "",
    desc: "",
    madeIn: "",

    taxId: "",
    taxPrice: "",
    prodSuppliers: [],
    splrCode: "",
    splrPrice: "",
    markup: "",
    minRtlPrice: "",
    rtlPrice: "",
    priceType: 1,
    retailPrices: [],

    trackInv: true,
    inventories: [],

    // Multi-Unit
    baseUnitId: "",
    productUnits: [],

    prodType: 1,
    // skuCode is the scannable BARCODE (skuType its symbology); sku is the shop's
    // own stock code. Two different fields — see the product form.
    skuCode: "",
    skuType: 1,
    sku: "",

    // Weighing scale: the short code keyed into the department scale, and whether
    // a scan carries the quantity instead of meaning "one of these".
    pluCode: "",
    sellByWeight: false,

    // Variant Product
    prodVariants: [],
    prodVarAttrs: [{ id: 1, attrId: "", value: [] }],

    // Composit Product
    compositProds: [],

    imgs: [],
    imgFiles: [],

    createDate: new Date().toISOString(),
    ...ctx,
  });

  const VariantProdMdl = () => ({
    // Not Mapped
    key: 0,

    id: 0,
    splrCode: "",
    splrPrice: "",
    minRtlPrice: "",
    rtlPrice: "",
    skuType: 1,
    skuCode: "",
    sku: "",
    enabled: true,

    taxId: "",
    taxPrice: "",

    attrId1: "",
    attrVal1: "",
    attrId2: "",
    attrVal2: "",
    attrId3: "",
    attrVal3: "",

    inventories: [],

    imgs: [],
    imgFiles: [],
  });

  const FollowUpTypeMdl = () => ({
    id: 0,
    name: "",
  });

  const InqSrcMdl = () => ({
    id: 0,
    name: "",
  });

  const InquiryMdl = () => ({
    id: 0,
    fullName: "",
    phone: "",
    inqSrcId: "",
    remark: "",

    email: "",
    address: "",
    subsTypeId: "",
    subsPlanId: "",
    inqDate: "",

    userId: "",
  });

  const CurrencyMdl = () => ({
    id: 0,
    symbol: "",
    shortName: "",
    fullName: "",
    currencyUnit: "",
    allowDecimal: 2,
    createDate: "",
    lastUpdate: "",
    createUserId: "",
    lastUpdateUserId: "",
    orgId: "",
    ...ctx,
  });

  const UserRoleMdl = () => ({
    id: "",
    title: "",
    subtitle: "",
    accessType: 0,
    featuresAccess: "",
    ...ctx,
  });

  const AccountTransferMdl = () => ({
    id: 0,
    fromAccountId: "",
    toAccountId: "",
    amount: "",
    note: "",
    // The shop's today, not the UTC date (which is yesterday or tomorrow near midnight).
    transferDate: storeTodayYmd(),
    ...ctx,
  });

  const CashSafeMdl = () => ({
    id: 0,
    name: "",
    code: "",
    chartOfAccountId: "",
    autoCreateAccount: true,
    custodianUserId: "",
    isDefault: false,
    isActive: true,
    allowNegative: false,
    description: "",
    ...ctx,
  });

  // Shared by the deposit and withdrawal forms — the direction is the endpoint,
  // not a field, so the same shape serves both.
  const CashSafeMovementMdl = () => ({
    cashSafeId: "",
    amount: "",
    sourceKind: "",
    bankAccountId: "",
    counterAccountId: "",
    referenceNo: "",
    note: "",
    ...ctx,
  });

  const CashSafeCountMdl = () => ({
    cashSafeId: "",
    countedAmount: "",
    note: "",
    ...ctx,
  });

  const UnitCategoryMdl = () => ({
    id: 0,
    name: "",
    desc: "",
    ...ctx,
  });

  const UnitMdl = () => ({
    id: 0,
    name: "",
    abbreviation: "",
    unitCategoryId: "",
    baseConversionFactor: 1,
    isBaseUnit: false,
    ...ctx,
  });

  const CommissionPlanMdl = () => ({
    id: 0,
    name: "",
    period: 1,
    tierMode: 1,
    deductVoidReturns: false,
    isActive: true,
    // 1 = Product, 2 = Service, 3 = Both. Default Both.
    targetTierScope: 3,
    applyToAllProducts: false,
    allProductCommission: "",
    allProductRateType: 2,
    applyToAllServices: false,
    allServiceCommission: "",
    allServiceRateType: 2,
    tiers: [],
    itemRates: [],
    ...ctx,
  });

  const CommissionReportFilterMdl = () => ({
    employeeId: "",
    fromDate: "",
    toDate: "",
    ...ctx,
  });

  return {
    JVMdl,
    InvItemCountedMdl,
    PassResetMdl,
    PinSetMdl,
    UserRoleMdl,
    CurrencyMdl,
    InquiryMdl,
    InqSrcMdl,
    FollowUpTypeMdl,
    PrintLabelMdl,
    CompanyMdl,
    LoginMdl,
    ChgPassMdl,
    ForgotMdl,
    ProdMdl,
    CategoryMdl,
    SupplierMdl,
    BrandMdl,
    StockMdl,
    ReturnStockMdl,
    StockTransMdl,
    CstmrMdl,
    CstmrGroupMdl,
    OutletMdl,
    PayTypeMdl,
    TaxMdl,
    CityMdl,
    UserMdl,
    CartProdMdl,
    QuoteMdl,
    ServiceMdl,
    ServiceItemMdl,
    ServiceContractMdl,
    ServiceContractLineMdl,
    ServiceCategoryMdl,
    ServiceDiscountTypeMdl,
    EmployeeMdl,
    ServiceBayMdl,
    CashMgmtMdl,
    SellMdl,
    SpecialOrderMdl,
    StockReturnMdl,
    RefundMdl,
    OrgMdl,
    TagMdl,
    VariantProdMdl,
    ReceivedDeliveryMdl,
    PurchaseInvoiceMdl,
    RegisterMdl,
    OpenRegMdl,
    SellRegisterMdl,
    CustomerTxnMdl,
    SellReturnMdl,
    DirectReturnMdl,
    SupplierTxnMdl,
    FollowUpMdl,
    InvCountMdl,
    ExpenseMdl,
    ExpenseTypeMdl,
    FiscalYearMdl,
    SnapshotMdl,
    LedgerMdl,
    CoaMdl,
    AccountMappingMdl,
    StockRequestMdl,
    StockDispatchMdl,

    InvAdjustmentMdl,
    StockWastageMdl,
    BizContextMdl,
    PriceCategoryMdl,
    AccountTransferMdl,
    BankAccountMdl,
    CashSafeMdl,
    CashSafeMovementMdl,
    CashSafeCountMdl,
    BankReconciliationMdl,
    UnitCategoryMdl,
    UnitMdl,
    CommissionPlanMdl,
    CommissionReportFilterMdl,
    PromotionMdl,
    PromotionConditionMdl,
    PromotionTierMdl,
  };
}
